import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { analytics } from './services/analytics';
import { bookAnalytics } from './services/bookAnalytics';
import type { ReaderEntrySource } from './services/bookAnalytics';
import type { AnalyticsInputMethod, InteractionContext } from './services/analyticsContext';
import {
  Book,
  findBookBySlug,
  extractBookSlugFromPath,
  extractReadBookSlugFromPath,
  getBookUrl,
  getReadUrl,
} from './utils/bookRouting';
import { fetchBooks, preloadReaderBook } from './services/booksApi';
import { readCachedCatalog } from './services/readerCacheStore';
import {
  getAudioProgressForBook,
  getEffectiveProgress,
  hydrateProgressFromServer,
  isBookInProgress,
  saveAudioProgress,
} from './utils/audioProgressUtils';
import type { AudioPosition } from './types/audioPosition';
import type { ChatSessionSummary } from './types/chat';
import { executeAudioControlCommand, type AudioControlCommand } from './types/audioControl';

import {
  ExplorePage,
  HomePage,
  LibraryPage,
  BookPage,
  SignupPage,
  EmailVerificationPage,
  LoginPage,
  ProfilePage,
  ForgotPasswordPage,
  ResetPasswordPage,
  TermsAndConditionsPage,
  PrivacyPage,
  AboutPage,
  TutorialPage,
  MyBooksPage,
  PricingPage
} from './views';
import BurgerMenuOverlay from './components/BurgerMenuOverlay';
import PersistentAudioPlayer from './components/PersistentAudioPlayer';
import TextChatSheet, { TextChatSheetHandle } from './components/TextChatSheet';
import Footer from './components/Footer';
import UsageLimitModal from './components/UsageLimitModal';
import CookieConsentBanner from './components/CookieConsentBanner';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { PlayerVisibilityProvider } from './contexts/PlayerVisibilityContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TutorialProvider, useTutorial, markTutorialCompleted } from './contexts/TutorialContext';
import TutorialOverlay from './components/TutorialOverlay';
import { useConsent } from './hooks/useConsent';
import { openConsentPreferences } from './services/consent';
import { useAudioManager } from './hooks/useAudioManager';
import { useNowPlaying } from './hooks/useNowPlaying';
import { useAppStoreBilling } from './hooks/useAppStoreBilling';
import { useBufferScheduler } from './hooks/useBufferScheduler';
import { useRTVIClient } from './hooks/useRTVIClient';
import { useConversationSessions } from './hooks/useConversationSessions';
import OAuthCallback from './components/OAuthCallback';
import AuthModal from './components/AuthModal';
import LoadingScreen from './components/LoadingScreen';
import SplashScreen from './views/SplashScreen';
import { authService } from './services/authService';
import { isNativeApp } from './utils/platform';
import { TEXT_CHAT_2XL_MIN_WIDTH_PX } from './utils/textChatLayout';
import { VOICE_CONVERSATION_FLUSH_DELAY_MS } from './utils/config';
import TopNavigation from './components/TopNavigation';

// Define view types
type ViewType =
  | 'home'
  | 'explore'
  | 'library'
  | 'book'
  | 'read'
  | 'signup'
  | 'email-verification'
  | 'login'
  | 'profile'
  | 'forgot-password'
  | 'reset-password'
  | 'terms'
  | 'privacy'
  | 'about'
  | 'tutorial'
  | 'my-books'
  | 'pricing'
  | 'oauth-callback-google'
  | 'oauth-callback-facebook'
  | 'oauth-callback-apple';

const normalizeReservedPath = (pathname: string): string => {
  if (pathname === '/aleph') {
    return '/';
  }

  if (pathname.startsWith('/aleph/')) {
    return pathname.slice('/aleph'.length);
  }

  return pathname;
};

const getReservedView = (pathname: string): ViewType | null => {
  const normalizedPath = normalizeReservedPath(pathname);

  switch (normalizedPath) {
    case '/':
      return 'home';
    case '/explore':
      return 'explore';
    case '/library':
      return 'library';
    case '/auth/callback/google':
    case '/oauth-complete/google':
      return 'oauth-callback-google';
    case '/auth/callback/facebook':
    case '/oauth-complete/facebook':
      return 'oauth-callback-facebook';
    case '/oauth-complete/apple':
      return 'oauth-callback-apple';
    case '/auth/verify':
    case '/email-verification':
      return 'email-verification';
    case '/auth/reset-password':
      return 'reset-password';
    case '/auth/forgot-password':
      return 'forgot-password';
    case '/profile':
      return 'profile';
    case '/my-books':
      return 'my-books';
    case '/terms':
      return 'terms';
    case '/privacy':
      return 'privacy';
    case '/about':
      return 'about';
    case '/tutorial':
      return 'tutorial';
    case '/pricing':
      return 'pricing';
    default:
      return null;
  }
};

const getLibrarySearchQueryFromLocation = (): string => {
  const normalizedPath = normalizeReservedPath(window.location.pathname);
  if (normalizedPath !== '/library') {
    return '';
  }

  return new URLSearchParams(window.location.search).get('q')?.trim() ?? '';
};

// How long sign-in waits for the server's progress before landing anyway.
// The request takes ~60-80ms on staging; this only bounds a bad connection.
const SIGN_IN_PROGRESS_TIMEOUT_MS = 1500;

const AppContent: React.FC = () => {
  const { theme } = useTheme();
  const { isAuthenticated, isLoading } = useAuth();
  const { status: analyticsConsent, source: analyticsConsentSource } = useConsent();
  const { start: startTutorial, isActive: tutorialActive, hasCompleted: tutorialCompleted } = useTutorial();
  const tutorialTriggeredRef = useRef(false);
  const prevTutorialActiveRef = useRef(false);
  // While the first-run tutorial is showing, hold audio playback until it ends.
  const [holdPlaybackForTutorial, setHoldPlaybackForTutorial] = useState(false);

  // Determine initial view based on URL
  const getInitialView = (): ViewType => {
    const path = window.location.pathname;
    const reservedView = getReservedView(path);
    if (reservedView) return reservedView;

    if (extractReadBookSlugFromPath(path)) return 'read';

    // Check if it's a book slug (but we need to wait for books to load to confirm)
    const bookSlug = extractBookSlugFromPath(path);
    if (bookSlug) return 'book'; // Tentatively set to book, will be confirmed when books load

    return 'home';
  };

  // View routing state
  const [currentView, setCurrentView] = useState<ViewType>(getInitialView());
  const [currentBookData, setCurrentBookData] = useState<Book | null>(null);
  // Seeded from disk so the shelf paints on the first frame instead of after a
  // round trip, and so a boot with no network still has a library to show.
  const [books, setBooks] = useState<Book[]>(() => readCachedCatalog() ?? []);
  const [booksLoading, setBooksLoading] = useState<boolean>(() => books.length === 0);
  // Set by sign-in while the landing waits on the server's progress: signing
  // out empties the device store, so deciding straight away always saw no
  // books and landed on Explore. 'ready' once progress is in (or timed out).
  const [signInLanding, setSignInLanding] = useState<'hydrating' | 'ready' | null>(null);
  const [previousView, setPreviousView] = useState<ViewType | null>(null);
  const [librarySearchQuery, setLibrarySearchQuery] = useState<string>(getLibrarySearchQueryFromLocation());

  // Track which book was last reported to Mixpanel to avoid duplicates in dev environment due to React Strict Mode
  const bookPageTrackedRef = useRef<string | null>(null);
  // Carries the click-time navigation source through to the Page View effect below, which is state-driven and decoupled from the click itself
  const bookOpenContextRef = useRef<{ source?: string; collectionName?: string }>({});
  // Same idea for the reader, whose entry points are spread across a click
  // handler, the docked player and URL routing. Defaults to the cold-load case.
  const readerEntrySourceRef = useRef<ReaderEntrySource>('direct_link');
  const readerPageTrackedRef = useRef<string | null>(null);

  // Auth modal state
  const [authModalView, setAuthModalView] = useState<'login' | 'signup' | null>(() =>
    normalizeReservedPath(window.location.pathname) === '/login' ? 'login' : null
  );
  const [authModalError, setAuthModalError] = useState<string | undefined>(undefined);
  const [loginEntrySource, setLoginEntrySource] = useState<string | undefined>(undefined);
  const [signupEntrySource, setSignupEntrySource] = useState<string | undefined>(undefined);

  // Global menu state
  const [showMenu, setShowMenu] = useState(false);
  const handleToggleMenu = useCallback(() => {
    setShowMenu((previous) => {
      const next = !previous;
      if (next) {
        setIsTextChatOpen(false);
      }
      return next;
    });
  }, []);

  // Audio state
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState<'local' | 'bot'>('local');
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const autoplayTargetBookRef = useRef<string | null>(null);
  const [isTextChatOpen, setIsTextChatOpen] = useState(false);
  const [textChatAnalyticsContext, setTextChatAnalyticsContext] = useState<InteractionContext>({
    surface: 'docked_player',
    trigger: 'button',
    input_method: 'unknown',
  });
  const [textChatOpenedFromReader, setTextChatOpenedFromReader] = useState(false);
  const [pendingTextChatInitialMessage, setPendingTextChatInitialMessage] = useState<string | null>(null);
  // The conversation a margin dot asked the sheet to open; null when the
  // sheet decides for itself from the playhead.
  const [textChatOpenAtSession, setTextChatOpenAtSession] = useState<ChatSessionSummary | null>(null);
  const [textChatDraft, setTextChatDraft] = useState('');
  const textChatSheetRef = useRef<TextChatSheetHandle>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load books data from API. The list is seeded synchronously from the
  // on-device catalog (see the useState above), so a cold start paints the
  // shelf immediately and a failed refresh leaves that list standing rather
  // than emptying it - an empty shelf reads as "your books are gone".
  useEffect(() => {
    const loadBooks = async () => {
      try {
        // No setBooksLoading(true) here: booksLoading already starts true when
        // nothing was seeded, and this effect runs once. Raising it again would
        // drop a spinner over a shelf that has already painted from cache.
        const booksData = await fetchBooks();
        setBooks(booksData);
      } catch (error) {
        console.error('Failed to load books:', error);
        analytics.trackErrorShown('book_catalog', error);
      } finally {
        setBooksLoading(false);
      }
    };

    loadBooks();
  }, []);

  // Don't auto-select any book - let user choose
  // (Removed auto-selection of Bartleby to fix audio initialization bug)

  // Kick off the first-run tutorial once the player has been evoked (via the
  // "Play" button). A short delay lets the open animation settle before the
  // spotlight attaches. start() no-ops if the tutorial was already completed.
  useEffect(() => {
    if (tutorialTriggeredRef.current) return;
    if (!(isReading && selectedBook && isAuthenticated)) return;
    const timer = window.setTimeout(() => {
      tutorialTriggeredRef.current = true;
      startTutorial();
    }, 500);
    return () => window.clearTimeout(timer);
  }, [isReading, selectedBook, isAuthenticated, startTutorial]);

  // Redirect authenticated users away from login/signup pages. Waits for auth
  // to settle: isAuthenticated is seeded from stored credentials, and closing
  // the modal on that guess loses it for good if the session turns out dead.
  useEffect(() => {
    if (!isLoading && isAuthenticated && (currentView === 'login' || currentView === 'signup' || authModalView)) {
      setAuthModalView(null);
      setAuthModalError(undefined);
    }
  }, [isLoading, isAuthenticated, currentView, authModalView]);

  // Handle OAuth errors in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      setAuthModalError(error);
      setAuthModalView('login');

      // Clean up the URL
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Redirect unauthenticated users away from protected pages
  useEffect(() => {
    if (!isLoading && !isAuthenticated && currentView === 'my-books') {
      setCurrentView('home');
      setCurrentBookData(null);
      window.history.replaceState({}, '', '/');
    }
  }, [isAuthenticated, currentView, isLoading]);

  useEffect(() => {
    if (isLoading || isAuthenticated || currentView !== 'read' || authModalView) return;
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    setLoginEntrySource('read_page');
    setAuthModalView('login');
  }, [authModalView, currentView, isAuthenticated, isLoading]);

  // Redirect authenticated users away from the homepage. On the web this always
  // lands on Explore. The native app has no marketing home, so it lands the user
  // inside the app: My Books when they have a book on the go, otherwise Explore.
  // A book still mid-read is also docked (paused), so the app opens ready to
  // resume rather than making them find their place again.
  useEffect(() => {
    if (isLoading || !isAuthenticated || currentView !== 'home') return;
    // A sign-in from the splash lands through navigateAfterSignIn instead.
    if (signInLanding) return;

    if (!isNativeApp()) {
      navigateToView('explore');
      return;
    }

    // Docking needs a catalog Book, not just a progress row, so wait for the
    // catalog — the effect re-runs when it lands (we are still on 'home').
    if (booksLoading) return;

    let cancelled = false;
    (async () => {
      let target: ViewType = 'explore';
      let resume: Book | null = null;
      try {
        // Hydrate rather than plain-fetch. The player resolves its resume point
        // — chapter and offset — from the LOCAL progress store, and reads it
        // exactly once, when the book is selected (useAudioManager's savedProgress
        // memo, which useChapterManager then commits per book id and never
        // revisits). AuthContext's initProgressSync is filling that store from
        // the same endpoint concurrently, so racing it meant docking against an
        // empty store: no chapterId, no offset, book pinned to chapter 1 second 0.
        // Merging the rows ourselves makes the store ready before we select.
        const rows = await hydrateProgressFromServer();
        // Mirror what My Books actually lists: a book appears there once it has
        // been started/finished (total_progress is a time value > 0) or favourited.
        // total_progress is on the same scale as book.totalDuration, NOT a 0..1
        // fraction — comparing it to 1 was the bug that always fell through to Explore.
        const hasMyBooks = rows.some(
          (row) => (row.total_progress || 0) > 0 || row.state?.isFavourite === true,
        );
        if (hasMyBooks) target = 'my-books';

        // Rows arrive ordered by last_played desc, so the first one still mid-book
        // is what they were listening to. Finished books and favourited-but-never-
        // started ones are skipped on purpose: there is nothing to resume.
        for (const row of rows) {
          const book = books.find((candidate) => candidate.id === row.book_id);
          if (book && isBookInProgress(book, row.total_progress || 0)) {
            resume = book;
            break;
          }
        }
      } catch {
        // Network/auth hiccup — fall back to Explore.
      }
      if (cancelled) return;

      // Dock it paused. The store is hydrated by now, so selecting the book is
      // all it takes — useChapterManager picks the saved chapter and
      // useAudioPosition seeks to the saved offset — and shouldAutoplay stays
      // false on purpose: launching the app must never start audio by itself.
      if (resume) {
        // A book already under way means this listener is not on their first
        // run, whatever this device's localStorage says. Settle that before the
        // player mounts: the tutorial fires on the player appearing (see the
        // effect above), and on a fresh install the server rows we just read are
        // the only thing that knows better — hasAnyStartedBook() still reads
        // false here if hydration hasn't landed yet.
        markTutorialCompleted();
        setSelectedBook(resume);
        setIsReading(true);
      }
      navigateToView(target);
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, currentView, isLoading, booksLoading, books, signInLanding]);

  // Stop audio playback when user logs out
  useEffect(() => {
    if (!isAuthenticated && isReading) {
      // Pause the audio element
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Reset reading state
      setIsReading(false);
    }
  }, [isAuthenticated]);

  // Audio manager setup
  const audioBook = selectedBook;
  const isReaderView = currentView === 'read'
    && isReading
    && currentBookData?.id === audioBook?.id;
  const playerAnalyticsSurface = isReaderView ? 'reader' : 'docked_player';
  // Read by the Start/Continue listener below, which outlives the render that
  // attached it: the surface changes as the user navigates between the reader
  // and the docked player, and the captured one goes stale.
  const playerAnalyticsSurfaceRef = useRef(playerAnalyticsSurface);
  playerAnalyticsSurfaceRef.current = playerAnalyticsSurface;
  const audioLegacyBookKeys = useMemo(() => {
    if (!audioBook) {
      return [];
    }
    return [audioBook.slug, audioBook.title].filter(Boolean);
  }, [audioBook?.slug, audioBook?.title]);

  const handleBookCompleted = useCallback(() => {
    if (!audioBook || audioBook.totalDuration <= 0) {
      return;
    }

    const savedProgress = getEffectiveProgress(getAudioProgressForBook(audioBook));
    if (savedProgress >= audioBook.totalDuration) {
      return;
    }

    const finishedPosition: AudioPosition = {
      sentenceIndex: -1,
      textFingerprint: '',
      timeOffset: 0,
      totalProgress: audioBook.totalDuration,
      lastPlayed: Date.now(),
      chapterId: undefined,
    };

    saveAudioProgress(audioBook.id, finishedPosition);
    bookAnalytics.trackCompletionStatusChange(audioBook, {
      finished: true,
      trigger: 'playback_completed',
      surface: playerAnalyticsSurface,
    });
  }, [audioBook, playerAnalyticsSurface]);

  const handleSleepTimerExpire = useCallback((minutes: number) => {
    if (selectedBook) {
      bookAnalytics.track('Sleep Timer Expired', selectedBook, {
        minutes,
        surface: playerAnalyticsSurface,
        trigger: 'automatic',
        input_method: 'automatic',
      });
    } else {
      analytics.track('Sleep Timer Expired', {
        minutes,
        surface: playerAnalyticsSurface,
        trigger: 'automatic',
        input_method: 'automatic',
      });
    }
  }, [playerAnalyticsSurface, selectedBook]);

  const audioManagerHook = useAudioManager({
    bookId: audioBook?.id || '',
    bookName: audioBook?.id || '',
    legacyBookKeys: audioLegacyBookKeys,
    onBookCompleted: handleBookCompleted,
    onSleepTimerExpire: handleSleepTimerExpire,
    // The reader only exists on /read for the book being played; everywhere
    // else the player is docked and there is no reader to gate playback on.
    isReaderAttached: isReaderView,
  });

  // Likewise the hook instance, so the same listener reads the current one
  // rather than whichever render happened to attach it.
  const audioManagerRef = useRef(audioManagerHook);
  audioManagerRef.current = audioManagerHook;

  const {
    audioRef,
    isPlaying,
    isReady,
    currentTime,
    duration,
    togglePlay,
    skipForward,
    skipBackward,
    seek,
    play,
    pause,
    nextChapter,
    previousChapter,
    primeForDeferredPlayback,
    rewindToCurrentSentence,
    currentChapter,
    chapters,
    goToChapter,
    totalCurrentTime,
    isPositionRestored,
    sleepTimer,
    sleepTimerRemainingSeconds,
    setCountdownSleepTimer,
    setEndOfChapterSleepTimer,
    cancelSleepTimer,
    playbackRate,
    setPlaybackRate,
    seekAndPlay,
    requestAutoplay,
  } = audioManagerHook;

  // A reader URL is a real entry point. Resolve it into the same persistent
  // player used by in-app playback, but never manufacture autoplay for a page
  // load or browser-history navigation.
  useEffect(() => {
    // Wait for auth to settle. isAuthenticated is seeded from stored
    // credentials and is true before the server has confirmed them, and
    // everything below is authenticated work with consequences: a progress
    // hydration request, and an isReading flip that mounts the player and
    // reports the reader Page View to Mixpanel. None of that should happen on
    // a guess that a dead session is about to overturn.
    if (isLoading) return;

    if (
      currentView !== 'read'
      || !isAuthenticated
      || !currentBookData
      || selectedBook?.id === currentBookData.id
    ) {
      // Guard on auth as well: without it, losing the session on /read (an
      // expired token on reload) has the logout effect above set isReading
      // false and this effect set it straight back in the same commit, which
      // leaves the route rendering neither the player nor the signed-out
      // fallback.
      if (
        isAuthenticated
        && currentView === 'read'
        && currentBookData
        && selectedBook?.id === currentBookData.id
      ) {
        setIsReading(true);
      }
      return;
    }

    let cancelled = false;
    audioRef.current?.pause();

    void hydrateProgressFromServer()
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        setSelectedBook(currentBookData);
        setIsReading(true);
      });

    return () => {
      cancelled = true;
    };
  }, [audioRef, currentBookData, currentView, isAuthenticated, isLoading, selectedBook?.id]);

  useEffect(() => {
    if (!isAuthenticated || !isReading) {
      cancelSleepTimer();
    }
  }, [cancelSleepTimer, isAuthenticated, isReading]);

  // App Store purchases (native app only): attach to StoreKit's out-of-band
  // deliveries from launch, and record the active entitlements at sign-in.
  // Inert until the account has bought something.
  useAppStoreBilling(isAuthenticated);

  // Lock-screen / Control Center controls + background audio (native app only).
  // Mirrors the docked-player mount condition so it's live exactly when a book is.
  useNowPlaying({
    enabled: isReading && !!audioBook && isAuthenticated,
    // Lock screen / Control Center: show the book title as the primary line and
    // the chapter as the secondary line (falls back to the author when a book
    // has no chapters).
    title: audioBook?.title || currentChapter?.title || '',
    artist: currentChapter?.title || audioBook?.author,
    album: audioBook?.title,
    artworkUrl: audioBook?.coverUrl,
    duration,
    position: currentTime,
    isPlaying,
    playbackRate,
    controls: {
      play,
      pause,
      skipForward: () => skipForward(10),
      skipBackward: () => skipBackward(10),
      nextTrack: () => { nextChapter(); },
      previousTrack: () => { previousChapter(); },
      seek,
    },
  });

  // The look-ahead buffer: keeps the next stretch of the open book on disk
  // while the user listens, so a lost signal degrades rather than stops.
  // Native only; a no-op everywhere else.
  useBufferScheduler({
    enabled: isReading && !!audioBook && isAuthenticated,
    book: audioManagerHook.readerBook,
    voiceKey: audioManagerHook.selectedReadingVoiceKey,
    currentChapterIndex: audioManagerHook.currentChapterIndex,
    positionSeconds: currentTime,
    isPlaying,
  });

  const audioControlHandlerRef = useRef<((command: AudioControlCommand) => void | Promise<void>) | null>(null);
  const dispatchAudioControl = useCallback((command: AudioControlCommand) =>
    audioControlHandlerRef.current?.(command), []);

  // RTVIClient hook
  const handleSpeakerChanged = (speaker: 'local' | 'bot') => {
    setCurrentSpeaker(speaker);
  };

  const {
    connect,
    disconnect,
    connectionStatus,
    isConnected,
    isBuffering,
    showUsageLimitModal,
    setShowUsageLimitModal,
    rtviClientRef,
  } = useRTVIClient(audioManagerHook, selectedBook, handleSpeakerChanged, dispatchAudioControl);

  // The reader's margin dots. Fetched when the reader shows a book, again
  // when the text-chat sheet closes (a conversation may have become
  // substantive while it was open) and shortly after a voice session ends
  // (the bot flushes its last rows on exit).
  const {
    sessions: conversationSessions,
    refresh: refreshConversationSessions,
  } = useConversationSessions(audioBook?.id ?? null, {
    enabled: isAuthenticated && isReading && isReaderView,
    refreshWhenFalse: [
      { active: isTextChatOpen },
      { active: isConnected, delayMs: VOICE_CONVERSATION_FLUSH_DELAY_MS },
    ],
  });

  // Track the paywall moment with Mixpanel
  useEffect(() => {
    if (showUsageLimitModal) {
      if (selectedBook) {
        bookAnalytics.track('Usage Limit Modal Shown', selectedBook, {
          surface: 'pricing_modal',
          source: playerAnalyticsSurface,
          trigger: 'automatic',
          input_method: 'automatic',
        });
      } else {
        analytics.track('Usage Limit Modal Shown', {
          surface: 'pricing_modal',
          source: playerAnalyticsSurface,
          trigger: 'automatic',
          input_method: 'automatic',
        });
      }
    }
  }, [showUsageLimitModal]);

  // Reset the visualizer's speaker indicator whenever a voice session ends. If a
  // session tears down while the bot is mid-utterance — e.g. the jump-to-passage
  // seek auto-disconnects while the bot is still speaking the "let me look that
  // up" filler — `onBotStoppedSpeaking` never fires, so `currentSpeaker` is left
  // stuck on 'bot' and the NEXT session's waveform opens in bot/Aleph color
  // instead of the user's blue. Forcing it back to 'local' on disconnect fixes
  // the cosmetic carry-over without affecting an active session.
  useEffect(() => {
    if (!isConnected) {
      setCurrentSpeaker('local');
    }
  }, [isConnected]);

  const handleAudioControl = useCallback((command: AudioControlCommand) => {
    executeAudioControlCommand(command, {
      rewindToCurrentSentence,
      play,
      pause,
      skipForward: () => skipForward(),
      skipBackward: () => skipBackward(),
      nextChapter,
      previousChapter,
      seekAndPlay,
    });
  }, [nextChapter, pause, play, previousChapter, rewindToCurrentSentence, seekAndPlay, skipBackward, skipForward]);
  audioControlHandlerRef.current = handleAudioControl;

  // View navigation functions
  const navigateToLibrary = (query?: string) => {
    const trimmedQuery = query?.trim() ?? '';

    setPreviousView(currentView);
    setCurrentView('library');
    setShowMenu(false);
    setLibrarySearchQuery(trimmedQuery);

    const nextUrl = trimmedQuery ? `/library?q=${encodeURIComponent(trimmedQuery)}` : '/library';
    window.history.pushState({}, '', nextUrl);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const navigateToView = (view: ViewType, source?: string) => {
    setPreviousView(currentView);
    if (view !== 'login' && view !== 'signup') {
      setCurrentView(view);
    } else {
      setAuthModalError(undefined);
      if (view === 'login') {
        setLoginEntrySource(source);
      } else if (view === 'signup') {
        setSignupEntrySource(source);
      }
    }
    setShowMenu(false); // Close menu when navigating

    // Update URL based on view
    switch (view) {
      case 'explore':
        window.history.pushState({}, '', '/explore');
        break;
      case 'library':
        setLibrarySearchQuery('');
        window.history.pushState({}, '', '/library');
        break;
      case 'profile':
        window.history.pushState({}, '', '/profile');
        break;
      case 'my-books':
        window.history.pushState({}, '', '/my-books');
        break;
      case 'signup':
        setAuthModalView('signup');
        break;
      case 'login':
        setAuthModalView('login');
        break;
      case 'email-verification':
        window.history.pushState({}, '', '/email-verification');
        break;
      case 'terms':
        window.history.pushState({}, '', '/terms');
        break;
      case 'privacy':
        window.history.pushState({}, '', '/privacy');
        break;
      case 'about':
        window.history.pushState({}, '', '/about');
        break;
      case 'tutorial':
        window.history.pushState({}, '', '/tutorial');
        break;
      case 'pricing':
        window.history.pushState({}, '', '/pricing');
        break;
      default:
        window.history.pushState({}, '', '/');
    }

    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  // Track page view with Mixpanel.
  // The reader branch below deliberately reads live player state without
  // depending on it: the effect fires on the entry transition, so the values it
  // closes over are the ones at the moment the reader was entered.
  useEffect(() => {
    if (!isReaderView) {
      readerPageTrackedRef.current = null;
    }

    if (booksLoading) {
      return;
    }

    if (
      currentView === 'oauth-callback-google'
      || currentView === 'oauth-callback-facebook'
      || currentView === 'oauth-callback-apple'
    ) {
      return;
    }

    const isBookViewAwaitingData = currentView === 'book' && !currentBookData;
    if (isBookViewAwaitingData) {
      return;
    }

    if (currentView === 'book' && currentBookData) {
      const slug = currentBookData.slug;

      if (bookPageTrackedRef.current === slug) {
        return; // already tracked this book page, avoid duplicate event due to React Strict Mode in dev environment
      }

      bookPageTrackedRef.current = slug;
      bookAnalytics.trackBookPageView(currentBookData, bookOpenContextRef.current);
      return;
    }

    if (currentView === 'read') {
      // Report the reader once it is genuinely live for this book, never on the
      // route alone: /read is also the URL behind the login wall a logged-out
      // visitor sees, and it is set before the reader effect has awaited the
      // progress hydration that the reported position is read from.
      if (!isReaderView || !currentBookData) {
        return;
      }

      const slug = currentBookData.slug;

      if (readerPageTrackedRef.current === slug) {
        return; // already tracked this reader entry (React Strict Mode / re-renders)
      }

      readerPageTrackedRef.current = slug;
      bookAnalytics.trackReaderPageView(currentBookData, {
        source: readerEntrySourceRef.current,
        isPlaying,
        isConnected,
        progressSeconds: audioManagerHook.getBookProgressSeconds(),
      });
      readerEntrySourceRef.current = 'direct_link';
      return;
    }

    const surfaceByView: Partial<Record<ViewType, InteractionContext['surface']>> = {
      library: 'library',
      'my-books': 'my_books',
      pricing: 'pricing_page',
    };
    analytics.track('Page View', {
      view: currentView,
      surface: surfaceByView[currentView],
      trigger: 'navigation',
      input_method: 'automatic',
    });

    // analyticsConsent is a dependency so the landing page view isn't lost: it
    // fires before the visitor has answered the banner, when analytics is still
    // gated and the event is dropped, and re-fires once they accept.
  }, [currentView, currentBookData, booksLoading, isReaderView, analyticsConsent]);

  // Mirror a decision made here to the server. The API gates on the user row,
  // so a withdrawal from the Profile page would otherwise leave the backend
  // tracking until the next login.
  //
  // Only for choices the visitor made themselves: a status that came from
  // reconciling with the server is exactly what we would be sending back.
  useEffect(() => {
    if (analyticsConsentSource !== 'user') return;
    if (!isAuthenticated || analyticsConsent === 'unset') return;

    authService.updateAnalyticsConsent(analyticsConsent === 'granted').catch(() => {
      // Best effort — the next login carries the decision regardless.
    });
  }, [analyticsConsent, analyticsConsentSource, isAuthenticated]);

  // Where a signed-in user lands: My Books when they have a book on the go,
  // otherwise Explore. Shared by sign-in and the logo so the two agree.
  // isBookInProgress reads this device's progress store, which the logo can
  // trust (the sign-in sync has run by then) but sign-in cannot: see
  // navigateAfterSignIn.
  const navigateToSignedInLanding = () => {
    const hasBooksInProgress = books.some(book => isBookInProgress(book));
    navigateToView(hasBooksInProgress ? 'my-books' : 'explore');
  };

  // Sign-in lands once the server's progress is in the device store, bounded
  // so a slow or failed request still lands (on Explore, if the store is
  // empty). The effect below does the navigating, so it sees the catalog as it
  // is then, not as it was when sign-in started.
  const navigateAfterSignIn = () => {
    setSignInLanding('hydrating');
    const timeout = new Promise<void>(resolve => window.setTimeout(resolve, SIGN_IN_PROGRESS_TIMEOUT_MS));
    void Promise.race([hydrateProgressFromServer(), timeout])
      .catch(() => undefined)
      .then(() => setSignInLanding('ready'));
  };

  useEffect(() => {
    if (signInLanding !== 'ready' || booksLoading) return;
    setSignInLanding(null);
    navigateToSignedInLanding();
  }, [signInLanding, booksLoading]);

  const navigateToHome = () => {
    // The splash is marketing for visitors who are signed out. Signed in, on
    // the web and in the app alike, the logo goes where signing in would.
    //
    // The device session is read here rather than the isAuthenticated state,
    // because signing out calls this as its very next statement (see
    // ProfilePage's handleLogout): the state captured in that closure still
    // says "signed in", which sent a user who had just signed out into the app
    // instead of the splash. The stored credentials are already gone by then.
    // Like AuthContext's seed, this ignores token expiry.
    if (authService.hasStoredCredentials()) {
      navigateToSignedInLanding();
      return;
    }

    setPreviousView(currentView);
    setCurrentView('home');
    setCurrentBookData(null);
    window.history.pushState({}, '', '/');
    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const preloadBookManifest = useCallback((bookId: string) => {
    if (!isAuthenticated || !bookId) return;
    void preloadReaderBook(bookId).catch((error) => {
      console.warn(`Failed to preload reader book ${bookId}:`, error);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (currentView === 'book' && currentBookData) {
      preloadBookManifest(currentBookData.id);
    }
  }, [currentBookData, currentView, preloadBookManifest]);

  // Navigate to specific book
  const navigateToBookBySlug = useCallback((bookSlug: string, source?: string, collectionName?: string) => {
    const book = findBookBySlug(books, bookSlug);
    if (book) {
      bookOpenContextRef.current = { source, collectionName };
      preloadBookManifest(book.id);
      setPreviousView(currentView);
      setCurrentBookData(book);
      setCurrentView('book');
      // Update URL without page reload
      window.history.pushState({}, '', getBookUrl(book.slug));
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  }, [books, currentView, preloadBookManifest]);

  const navigateToReader = useCallback((bookSlug: string) => {
    const book = findBookBySlug(books, bookSlug);
    if (!book) return;
    readerEntrySourceRef.current = 'docked_player';
    preloadBookManifest(book.id);
    setCurrentBookData(book);
    setCurrentView('read');
    setShowMenu(false);
    window.history.pushState({}, '', getReadUrl(book.slug));
    window.scrollTo({ top: 0 });
  }, [books, preloadBookManifest]);

  // Handle start reading action
  const handleStartReading = (bookSlug: string) => {
    const book = findBookBySlug(books, bookSlug);
    if (book) {
      readerEntrySourceRef.current = currentView === 'my-books' ? 'my_books' : 'book_page';
      if (!isAuthenticated) {
        const readUrl = getReadUrl(book.slug);
        sessionStorage.setItem('redirectAfterLogin', readUrl);
        setCurrentBookData(book);
        setCurrentView('read');
        window.history.pushState({}, '', readUrl);
        navigateToView('signup', 'start_listening');
        return;
      }

      if (isMobile) {
        // Authorize this persistent media element inside the original tap.
        // Manifest loading, position restoration and the real play request all
        // happen later and would otherwise be blocked by mobile WebKit.
        primeForDeferredPlayback();
      }
      preloadBookManifest(book.id);
      setSelectedBook(book);
      setIsReading(true);
      setCurrentBookData(book);
      setCurrentView('read');
      window.history.pushState({}, '', getReadUrl(book.slug));
      autoplayTargetBookRef.current = book.id; // Track which book we want to autoplay
      if (tutorialCompleted()) {
        setShouldAutoplay(true); // Always autoplay when user clicks Start/Continue Listening
      } else {
        // First run: the tutorial is about to appear. Hold playback until the
        // user finishes (or skips) it, then autoplay.
        setHoldPlaybackForTutorial(true);
        setShouldAutoplay(false);
      }
    }
  };

  // Handle toggle play
  const handleTogglePlay = () => {
    if (!isReading && selectedBook) {
      setIsReading(true);
    }
    togglePlay();
  };

  // Open the text-chat sheet. Pauses playback by default (same as voice), though
  // the user may resume while chatting.
  const handleOpenTextChat = useCallback((
    initialMessage?: string,
    forceOpen = false,
    context?: InteractionContext,
  ) => {
    const trimmedInitialMessage = initialMessage?.trim() || '';
    if (window.innerWidth < 1024 && !trimmedInitialMessage && !forceOpen) return;
    const usesConcurrentReaderChat = isReaderView
      && window.innerWidth >= TEXT_CHAT_2XL_MIN_WIDTH_PX;
    if (!usesConcurrentReaderChat) {
      audioRef.current?.pause();
    }
    // The chat sheet stacks above the burger overlay; close the burger so it
    // isn't left open and hidden underneath.
    setShowMenu(false);
    // Establish the source-specific sheet layout while it is still closed so
    // its opening animation starts on the correct axis.
    flushSync(() => {
      setTextChatOpenedFromReader(isReaderView);
    });
    flushSync(() => {
      setTextChatOpenAtSession(null);
      setPendingTextChatInitialMessage(trimmedInitialMessage || null);
      setTextChatAnalyticsContext(context ?? {
        surface: playerAnalyticsSurface,
        trigger: trimmedInitialMessage ? 'automatic' : 'button',
        input_method: trimmedInitialMessage ? 'automatic' : 'unknown',
      });
      setIsTextChatOpen(true);
    });
    textChatSheetRef.current?.focusComposer();
  }, [audioRef, isReaderView, playerAnalyticsSurface]);

  // Open the sheet at the conversation a margin dot stands for. Same
  // preamble as handleOpenTextChat without its width gate: that gate is for
  // the composer entry point, and a dot tap is always an open. The composer
  // is not focused: the user is reading, not typing, so the keyboard stays
  // down.
  const handleOpenConversationFromMargin = useCallback((
    session: ChatSessionSummary,
    inputMethod: AnalyticsInputMethod,
  ) => {
    const usesConcurrentReaderChat = isReaderView
      && window.innerWidth >= TEXT_CHAT_2XL_MIN_WIDTH_PX;
    if (!usesConcurrentReaderChat) {
      audioRef.current?.pause();
    }
    setShowMenu(false);
    flushSync(() => {
      setTextChatOpenedFromReader(isReaderView);
    });
    // The sheet's open effect reads openAtSession from the render in which
    // isOpen flipped, so both must land in the same commit.
    flushSync(() => {
      setTextChatOpenAtSession(session);
      setPendingTextChatInitialMessage(null);
      setTextChatAnalyticsContext({
        surface: playerAnalyticsSurface,
        trigger: 'margin_dot',
        input_method: inputMethod,
      });
      setIsTextChatOpen(true);
    });
  }, [audioRef, isReaderView, playerAnalyticsSurface]);

  // A conversation removed from the sheet's history: its dot goes, and if a
  // dot had opened it, the halo goes with it.
  const handleConversationRemoved = useCallback((sessionId: number) => {
    setTextChatOpenAtSession((current) => (current?.id === sessionId ? null : current));
    refreshConversationSessions();
  }, [refreshConversationSessions]);

  const handleCloseTextChat = useCallback((context?: InteractionContext) => {
    if (context) {
      setTextChatAnalyticsContext(context);
    }
    setIsTextChatOpen(false);
    setPendingTextChatInitialMessage(null);
    setTextChatOpenAtSession(null);
  }, []);

  useEffect(() => {
    if (isReaderView || !isTextChatOpen || !textChatOpenedFromReader) return;
    handleCloseTextChat({
      surface: 'reader',
      trigger: 'navigation',
      input_method: 'automatic',
    });
  }, [handleCloseTextChat, isReaderView, isTextChatOpen, textChatOpenedFromReader]);

  // Handle book finished (from MyBooksPage)
  const handleBookFinished = (bookId: string) => {
    if (selectedBook && selectedBook.id === bookId) {
      // Pause audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      // Close player and clear selection
      setIsReading(false);
      setSelectedBook(null);
    }
  };

  // Autoplay when the player opens. This is only the outer "should we even
  // attempt autoplay for this book" trigger — the actual playback intent
  // (waiting for reader content/position readiness) is owned by the audio
  // manager via requestAutoplay(), which is one-shot on its own, so the flag
  // here is retired immediately rather than deferred to the book-change reset
  // effect below (that effect remains only as a safety net for the case where
  // the player is closed before autoplay ever fires).
  useEffect(() => {
    if (shouldAutoplay && !holdPlaybackForTutorial &&
      selectedBook && selectedBook.id === autoplayTargetBookRef.current) {
      requestAutoplay();
      setShouldAutoplay(false);
    }
  }, [shouldAutoplay, holdPlaybackForTutorial, requestAutoplay, selectedBook]);

  // Release the held playback once the tutorial finishes (or is skipped).
  useEffect(() => {
    if (prevTutorialActiveRef.current && !tutorialActive && holdPlaybackForTutorial) {
      setHoldPlaybackForTutorial(false);
      setShouldAutoplay(true);
    }
    prevTutorialActiveRef.current = tutorialActive;
  }, [tutorialActive, holdPlaybackForTutorial]);

  // Reset autoplay flag when book changes OR when player closes
  useEffect(() => {
    // When selectedBook changes and it's different from the target, reset the flag
    if (selectedBook && selectedBook.id !== autoplayTargetBookRef.current) {
      setShouldAutoplay(false);
      autoplayTargetBookRef.current = null;
    }
    // Also reset when player closes (isReading becomes false)
    if (!isReading && autoplayTargetBookRef.current !== null) {
      setShouldAutoplay(false);
      autoplayTargetBookRef.current = null;
    }
    if (!isReading && holdPlaybackForTutorial) {
      setHoldPlaybackForTutorial(false);
    }
  }, [selectedBook, isReading, holdPlaybackForTutorial]);

  // Handle Start/Continue Listening Button Mixpanel tracking
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement || !selectedBook) return;
    const handleTimeUpdate = () => {
      const newDuration = audioElement.duration;
      if (newDuration > 0) {
        const progressSeconds = audioManagerRef.current.getBookProgressSeconds();
        bookAnalytics.track('Start/Continue Listening Button', selectedBook, {
          surface: playerAnalyticsSurfaceRef.current,
          trigger: 'button',
          input_method: 'unknown',
        }, progressSeconds);
        // Remove the event listener after the first time update to avoid repeated tracking
        audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [selectedBook?.id, audioRef]);

  // Handle back navigation
  const handleBack = () => {
    if (previousView) {
      // Navigate to the previous view
      setCurrentView(previousView);
      setCurrentBookData(null);

      // Update URL based on previous view
      switch (previousView) {
        case 'home':
          window.history.pushState({}, '', '/');
          break;
        case 'explore':
          window.history.pushState({}, '', '/explore');
          break;
        case 'library':
          setLibrarySearchQuery('');
          window.history.pushState({}, '', '/library');
          break;
        case 'profile':
          window.history.pushState({}, '', '/profile');
          break;
        case 'login':
          setAuthModalView('login');
          break;
        case 'signup':
          setAuthModalView('signup');
          break;
        default:
          window.history.pushState({}, '', '/');
      }

      // Clear previous view after navigation
      setPreviousView(null);
    } else {
      // Fallback to browser back if no previous view
      window.history.back();
    }
  };

  // Listen for URL changes to update view
  useEffect(() => {
    // Called with an event for real back/forward navigation, and without one
    // when this effect resolves the URL on mount or after books load.
    const handlePopState = (event?: PopStateEvent) => {
      const path = window.location.pathname;
      const reservedView = getReservedView(path);

      // Reserved routes should win only on exact path matches.
      if (reservedView) {
        setCurrentView(reservedView);
        setCurrentBookData(null);
        setLibrarySearchQuery(reservedView === 'library' ? getLibrarySearchQueryFromLocation() : '');
        return;
      }

      const readBookSlug = extractReadBookSlugFromPath(path);
      if (readBookSlug) {
        readerEntrySourceRef.current = event ? 'history' : 'direct_link';
        setCurrentView('read');
        setLibrarySearchQuery('');
        if (books.length > 0) {
          setCurrentBookData(findBookBySlug(books, readBookSlug) ?? null);
        }
        return;
      }

      // Check if it's a book slug
      const bookSlug = extractBookSlugFromPath(path);
      if (bookSlug) {
        if (books.length > 0) {
          const book = findBookBySlug(books, bookSlug);
          if (book) {
            bookOpenContextRef.current = {};
            setCurrentBookData(book);
            setCurrentView('book');
            return;
          }
          // If bookSlug exists but book not found, could be invalid URL
          // Don't change view in this case, let the BookPage handle the error
        } else {
          // Books haven't loaded yet, but we detected a book slug
          // Don't change the view (it's already set to 'book' from getInitialView)
          return;
        }
      }
    };

    // Set initial view based on URL
    handlePopState();

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [books]); // Add books dependency

  // Render current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'home':
        // Native app: no marketing home — show the branded splash / login entry.
        // (Authenticated users are routed into the app by the home-view effect.)
        if (isNativeApp()) {
          return <SplashScreen
            isLoading={isLoading}
            isAuthenticated={isAuthenticated}
            onNavigateToLogin={(source?: string) => navigateToView('login', source)}
            onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          />;
        }
        // Synchronous check to avoid flash of content
        const isLikelyAuthenticated = authService.isAuthenticated();
        if ((isLoading && isLikelyAuthenticated) || isAuthenticated) {
          return <LoadingScreen />;
        }

        return <HomePage
          books={books}
          onNavigateHome={navigateToHome}
          onJoinWaitlist={() => navigateToView('signup', 'signup_button')}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToTerms={() => navigateToView('terms')}
          onNavigateToPrivacy={() => navigateToView('privacy')}
          onNavigateToAbout={() => navigateToView('about')}
          onNavigateToPricing={() => navigateToView('pricing')}
          onToggleMenu={handleToggleMenu}
          onBookSelect={navigateToBookBySlug}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
        />;
      case 'explore':
        return <ExplorePage
          books={books}
          onNavigateHome={navigateToHome}
          onBookSelect={navigateToBookBySlug}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
        />;
      case 'library':
        return <LibraryPage
          books={books}
          initialSearchQuery={librarySearchQuery}
          onNavigateHome={navigateToHome}
          onBookSelect={navigateToBookBySlug}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onLibrarySearch={navigateToLibrary}
          onToggleMenu={handleToggleMenu}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
        />;
      case 'book':
        // Check if the current book matches the book being viewed
        const isCurrentBook = currentBookData && audioBook && currentBookData.id === audioBook.id;
        const botTime = isCurrentBook ? totalCurrentTime : 0;

        return <BookPage
          book={currentBookData}
          onNavigateHome={navigateToHome}
          booksLoading={booksLoading}
          onStartReading={handleStartReading}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
          currentTime={botTime}
          onBack={handleBack}
          onConnect={connect}
          onDisconnect={disconnect}
          isConnected={isConnected}
          connectionStatus={connectionStatus}
          isBuffering={isBuffering}
          isReady={isReady}
          isPositionRestored={isPositionRestored}
          duration={duration}
          skipForward={skipForward}
          skipBackward={skipBackward}
          isLoggedIn={isAuthenticated}
          isLoading={isLoading}
          isPlaying={isPlaying}
          onTogglePlay={handleTogglePlay}
          isCurrentBook={isCurrentBook || false}
        />;
      case 'read':
        // The persistent player owns the dedicated reader surface outside this
        // page switch so changing routes never remounts its audio state.
        return null;
      case 'email-verification':
        return <EmailVerificationPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
        />;
      case 'profile':
        return <ProfilePage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToPricing={() => navigateToView('pricing')}
          onToggleMenu={handleToggleMenu}
          isLoading={isLoading}
          offlineDevContext={
            audioManagerHook.readerBook && currentChapter && audioManagerHook.selectedReadingVoiceKey
              ? {
                  book: audioManagerHook.readerBook,
                  chapter: currentChapter,
                  voiceKey: audioManagerHook.selectedReadingVoiceKey,
                }
              : null
          }
        />;
      case 'my-books':
        return <MyBooksPage
          books={books}
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
          onBookSelect={navigateToBookBySlug}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
          onStartReading={handleStartReading}
          onPreloadBook={preloadBookManifest}
          currentBook={selectedBook}
          isPlaying={isPlaying}
          onTogglePlay={handleTogglePlay}
          currentTime={totalCurrentTime}
          isReady={isReady}
          isPositionRestored={isPositionRestored}
          onBookFinished={handleBookFinished}
          loading={booksLoading}
        />;
      case 'forgot-password':
        return <ForgotPasswordPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
        />;
      case 'reset-password':
        return <ResetPasswordPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToForgotPassword={() => navigateToView('forgot-password')}
          onToggleMenu={handleToggleMenu}
        />;
      case 'terms':
        return <TermsAndConditionsPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToPrivacy={() => navigateToView('privacy')}
          onToggleMenu={handleToggleMenu}
          isAuthenticated={isAuthenticated}
        />;
      case 'privacy':
        return <PrivacyPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToTerms={() => navigateToView('terms')}
          onToggleMenu={handleToggleMenu}
          isAuthenticated={isAuthenticated}
        />;
      case 'about':
        return <AboutPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
          onBookSelect={navigateToBookBySlug}
          isAuthenticated={isAuthenticated}
        />;
      case 'tutorial':
        return <TutorialPage
          books={books}
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onToggleMenu={handleToggleMenu}
          onBookSelect={navigateToBookBySlug}
          isAuthenticated={isAuthenticated}
        />;
      case 'pricing':
        return <PricingPage
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToTerms={() => navigateToView('terms')}
          onNavigateToPrivacy={() => navigateToView('privacy')}
          showMenu={showMenu}
          onToggleMenu={handleToggleMenu}
          isAuthenticated={isAuthenticated}
        />;
      case 'oauth-callback-google':
        return <OAuthCallback
          provider="google"
          onSuccess={navigateAfterSignIn}
          onError={() => navigateToView('login')}
        />;
      case 'oauth-callback-facebook':
        return <OAuthCallback
          provider="facebook"
          onSuccess={navigateAfterSignIn}
          onError={() => navigateToView('login')}
        />;
      case 'oauth-callback-apple':
        return <OAuthCallback
          provider="apple"
          onSuccess={navigateAfterSignIn}
          onError={() => navigateToView('login')}
        />;
      default:
        // Native app: fall back to the splash / login entry, never the marketing home.
        if (isNativeApp()) {
          return <SplashScreen
            isLoading={isLoading}
            isAuthenticated={isAuthenticated}
            onNavigateToLogin={(source?: string) => navigateToView('login', source)}
            onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          />;
        }
        return <HomePage
          books={books}
          onNavigateHome={navigateToHome}
          onJoinWaitlist={() => navigateToView('signup', 'signup_button')}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToProfile={() => navigateToView('profile')}
          onNavigateToLogin={(source?: string) => navigateToView('login', source)}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToTerms={() => navigateToView('terms')}
          onNavigateToPrivacy={() => navigateToView('privacy')}
          onNavigateToAbout={() => navigateToView('about')}
          onNavigateToPricing={() => navigateToView('pricing')}
          onToggleMenu={handleToggleMenu}
          onBookSelect={navigateToBookBySlug}
          isAuthenticated={isAuthenticated}
        />;
    }
  };

  return (
    <PlayerVisibilityProvider isPersistentPlayerOpen={isReading && !!audioBook && isAuthenticated}>
    <div
      className={`flex flex-col min-h-dvh ${theme === 'dark' ? 'dark' : ''}`}
      style={{ backgroundColor: theme === 'dark' ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}
    >
      {/* ARIA live region for dynamic announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {/* Dynamic announcements will be inserted here */}
      </div>
      {currentView === 'read' && (
        <TopNavigation
          key="reader-navigation"
          isDarkMode={theme === 'dark'}
          onToggleMenu={handleToggleMenu}
          onNavigateHome={navigateToHome}
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToLibrary()}
          onNavigateToMyBooks={() => navigateToView('my-books')}
          onNavigateToProfile={() => navigateToView('profile')}
          onBookSelect={navigateToBookBySlug}
          onLibrarySearch={navigateToLibrary}
          showProfileIcon={isAuthenticated}
          isLoading={isLoading}
          compactNav
        />
      )}
      {currentView !== 'read' && (
        <main
          key="page-content"
          role="main"
          id="main-content"
          className={`grow px-safe ${currentView !== 'home' ? 'pt-safe-nav' : ''}`}
        >
          {renderCurrentView()}
        </main>
      )}
      {currentView === 'read' && (!currentBookData || !isReading || audioBook?.id !== currentBookData.id) && (
        <main className="mt-[calc(4.25rem_+_var(--safe-top))] flex h-[calc(100dvh-4.25rem-var(--safe-top))] items-center justify-center px-safe p-8">
          <h1 className="text-2xl">
            {(() => {
              if (!isAuthenticated) return 'Sign in to read';
              if (booksLoading || currentBookData) return 'Loading reader…';
              // The catalog lands one commit before the URL-routing effect can
              // resolve the slug into currentBookData, so state alone reports a
              // frame of "Book not found" for a book that is about to open
              // fine. Ask the catalog directly to cover that gap.
              const slug = extractReadBookSlugFromPath(window.location.pathname);
              return slug && findBookBySlug(books, slug) ? 'Loading reader…' : 'Book not found';
            })()}
          </h1>
        </main>
      )}
      <BurgerMenuOverlay
        isOpen={showMenu}
        onClose={() => setShowMenu(false)}
        onNavigateToExplore={() => navigateToView('explore')}
        onNavigateToLibrary={() => navigateToView('library')}
        onNavigateToMyBooks={() => navigateToView('my-books')}
        onNavigateToProfile={() => navigateToView('profile')}
        onNavigateToLogin={(source?: string) => navigateToView('login', source)}
        onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
        onNavigateToTerms={() => navigateToView('terms')}
        onNavigateToPrivacy={() => navigateToView('privacy')}
        onNavigateToAbout={() => navigateToView('about')}
        onNavigateToPricing={() => navigateToView('pricing')}
        isAuthenticated={isAuthenticated}
      />
      {/* Audio element - persists even when player UI is hidden */}
      <audio key="persistent-audio" ref={audioRef} className="hidden" />
      {/* Persistent Audio Player - Only for logged in users */}
      {isReading && audioBook && isAuthenticated && (
        <PersistentAudioPlayer
          key="persistent-player"
          book={audioBook}
          audioRef={audioRef}
          isPlaying={isPlaying}
          isReady={isReady}
          currentTime={currentTime}
          duration={duration}
          togglePlay={togglePlay}
          skipForward={skipForward}
          skipBackward={skipBackward}
          onConnect={connect}
          onDisconnect={disconnect}
          isConnected={isConnected}
          connectionStatus={connectionStatus}
          isBuffering={isBuffering}
          onSeek={seek}
          currentChapter={currentChapter}
          chapters={chapters}
          onChapterSelect={(chapterId, options) => {
            const chapterIndex = chapters.findIndex(c => c.id === chapterId);
            if (chapterIndex !== -1) {
              goToChapter(chapterIndex, { fragmentId: options?.fragmentId });
            }
          }}
          rtviClientRef={rtviClientRef}
          currentSpeaker={currentSpeaker}
          mode={isReaderView ? 'reader' : 'docked'}
          onOpenReader={navigateToReader}
          onNavigateToBook={navigateToBookBySlug}
          readerBook={audioManagerHook.readerBook}
          isRenderProfileSupported={audioManagerHook.isRenderProfileSupported}
          chapterRuntime={audioManagerHook.chapterRuntime}
          chapterTimingsStatus={audioManagerHook.chapterTimingsStatus}
          selectedReadingVoiceKey={audioManagerHook.selectedReadingVoiceKey}
          isLoadingReaderBook={audioManagerHook.isLoadingBook}
          readerBookError={audioManagerHook.readerBookError}
          onRetryReaderBook={audioManagerHook.retryReaderBook}
          chapterRecoveryNotice={audioManagerHook.chapterRecoveryNotice}
          positionRequest={audioManagerHook.positionRequest}
          pendingResumeSeconds={audioManagerHook.pendingResumeSeconds}
          onReaderContentReadinessChange={audioManagerHook.onReaderContentReadinessChange}
          onReaderPositioned={audioManagerHook.onReaderPositioned}
          onSeekAndPlay={seekAndPlay}
          getBookProgressSeconds={audioManagerHook.getBookProgressSeconds}
          onOpenTextChat={handleOpenTextChat}
          isTextChatOpen={isTextChatOpen}
          textChatDraft={textChatDraft}
          conversationSessions={conversationSessions}
          activeConversationId={isTextChatOpen ? textChatOpenAtSession?.id ?? null : null}
          onOpenConversation={handleOpenConversationFromMargin}
          sleepTimer={sleepTimer}
          sleepTimerRemainingSeconds={sleepTimerRemainingSeconds}
          onSetCountdownSleepTimer={setCountdownSleepTimer}
          onSetEndOfChapterSleepTimer={setEndOfChapterSleepTimer}
          onCancelSleepTimer={cancelSleepTimer}
          playbackRate={playbackRate}
          onPlaybackRateChange={setPlaybackRate}
        />
      )}
      {/* Text-chat sheet - mounted alongside the player so it tracks max-read progress */}
      {isReading && audioBook && isAuthenticated && (
        <TextChatSheet
          ref={textChatSheetRef}
          isOpen={isTextChatOpen}
          onClose={handleCloseTextChat}
          analyticsContext={textChatAnalyticsContext}
          book={audioBook}
          audio={audioManagerHook}
          totalBookDuration={audioBook.totalDuration}
          initialMessage={pendingTextChatInitialMessage}
          openAtSession={textChatOpenAtSession}
          onConversationRemoved={handleConversationRemoved}
          onDraftChange={setTextChatDraft}
          constrainMobileWidth={textChatOpenedFromReader}
          allowConcurrentReaderInteraction={isReaderView}
        />
      )}
      {/* Footer - Only show if NOT on home page, as HomePage handles its own footer for ScrollSmoother */}
      {currentView !== 'home' && currentView !== 'read' && (
        <Footer
          onNavigateToExplore={() => navigateToView('explore')}
          onNavigateToLibrary={() => navigateToView('library')}
          onNavigateToSignup={(source?: string) => navigateToView('signup', source)}
          onNavigateToTerms={() => navigateToView('terms')}
          onNavigateToPrivacy={() => navigateToView('privacy')}
          onNavigateToAbout={() => navigateToView('about')}
          onNavigateToPricing={() => navigateToView('pricing')}
          onOpenCookiePreferences={openConsentPreferences}
        />
      )}
      {/* Consent banner - Global, so an undecided visitor is asked wherever they
          land rather than only on the home page. Portals to document.body. */}
      <CookieConsentBanner onNavigateToPrivacyPolicy={() => navigateToView('privacy')} />
      {/* Usage Limit Modal - Global */}
      <UsageLimitModal
        isOpen={showUsageLimitModal}
        onClose={() => setShowUsageLimitModal(false)}
        onNavigateToTerms={() => { setShowUsageLimitModal(false); navigateToView('terms'); }}
        onNavigateToPrivacy={() => { setShowUsageLimitModal(false); navigateToView('privacy'); }}
      />
      {/* Auth Modal. Held shut (not cleared) while a stored session is still
          being validated, so a signed-in user never sees the form flash or
          fires LoginPage's mount-time "Login Modal" event; if validation
          fails, the preserved view opens. */}
      <AuthModal
        isOpen={!!authModalView && !(isLoading && isAuthenticated)}
        onClose={() => {
          setAuthModalView(null);
          setAuthModalError(undefined);
          if (currentView === 'read' && !isAuthenticated) {
            sessionStorage.removeItem('redirectAfterLogin');
            // The wall opens as soon as auth resolves, which can beat the
            // catalog, so read the slug off the URL rather than relying on
            // currentBookData. The book itself is filled in here when the
            // catalog is ready, and by the URL routing effect when it is not.
            const slug = currentBookData?.slug
              ?? extractReadBookSlugFromPath(window.location.pathname);
            if (slug) {
              window.history.replaceState({}, '', getBookUrl(slug));
              setCurrentBookData(currentBookData ?? findBookBySlug(books, slug) ?? null);
              setCurrentView('book');
            } else {
              setCurrentView('library');
              window.history.replaceState({}, '', '/library');
            }
          }
        }}
      >
        {authModalView === 'login' && (
          <LoginPage
            entrySource={loginEntrySource}
            onNavigateToSignup={(source?: string) => {
              setAuthModalError(undefined);
              setAuthModalView('signup');
              setSignupEntrySource(source);
            }}
            onLoginSuccess={() => {
              setAuthModalView(null);
              setAuthModalError(undefined);

              const savedPath = sessionStorage.getItem('redirectAfterLogin');
              if (savedPath && extractReadBookSlugFromPath(savedPath)) {
                sessionStorage.removeItem('redirectAfterLogin');
                window.history.replaceState({}, '', savedPath);
                window.dispatchEvent(new PopStateEvent('popstate'));
                // The listener above ran synchronously and labelled this as
                // back/forward navigation; it is really the post-login landing.
                readerEntrySourceRef.current = 'post_login';
                return;
              }

              navigateAfterSignIn();
            }}
            onNavigateToForgotPassword={() => {
              setAuthModalView(null);
              setAuthModalError(undefined);
              navigateToView('forgot-password');
            }}
          />
        )}
        {authModalView === 'signup' && (
          <SignupPage
            initialError={authModalError}
            entrySource={signupEntrySource}
            onNavigateToLogin={(source?: string) => {
              setAuthModalError(undefined);
              setAuthModalView('login');
              setLoginEntrySource(source);
            }}
            onNavigateToTerms={() => {
              setAuthModalView(null);
              setAuthModalError(undefined);
              navigateToView('terms');
            }}
            onNavigateToPrivacy={() => {
              setAuthModalView(null);
              setAuthModalError(undefined);
              navigateToView('privacy');
            }}
            onSignupSuccess={() => {
              setAuthModalError(undefined);
              // Registration does not authenticate — the user verifies and
              // signs in later. Leaving this set would send an unrelated OAuth
              // sign-in later in this tab to whatever they were reading now.
              sessionStorage.removeItem('redirectAfterLogin');
            }}
          />
        )}
      </AuthModal>
      {/* First-run interactive tutorial */}
      <TutorialOverlay />
    </div>
    </PlayerVisibilityProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TutorialProvider>
          <AppContent />
        </TutorialProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
